import React, { useState, useEffect, useRef } from 'react';
import { getProfiles } from '../../services/api';
import './ClientSidebar.css';
import BtcCounsellorSelector from './BtcCounsellorSelector';
import { useLanguage } from '../../LanguageContext';
import ClientSidebarHeader from './ClientSidebarHeader';
import ClientWheelSelector from './ClientWheelSelector';
import OarsSidebarPanel from './OarsSidebarPanel';
import SpecificStrategiesSidebarPanel from './SpecificStrategiesSidebarPanel';
import ReportFullSessionAnalytics from '../report/ReportFullSessionAnalytics';
import ReportDownloadActions from '../report/ReportDownloadActions';
import WorkingAllianceChart from '../report/WorkingAllianceChart';
import '../report/ReportFullSessionPostSessionPanel.css';
import { CLIENT_MODE_COUNSELLOR_OPTIONS } from '../../counsellorModes';

function CompactLoadingDots() {
  return (
    <span className="loading-dots">
      <span /><span /><span />
    </span>
  );
}

function CompactAnalyticsMessage({ msg }) {
  return (
    <div className="analytics-message">
      <span className="analytics-bullet">•</span>
      <span className="analytics-text">{msg}</span>
    </div>
  );
}

// Live-session observers in the single-column drawer. Open/closed state is the
// report panel's own (WAI starts closed and opens once it has a score), so the
// phone layout behaves like the 3-column one.
function CompactSessionObserverTabs({ t, compactSessionObserverStatus, onShowWorkingAlliance }) {
  if (!compactSessionObserverStatus) {
    return null;
  }

  const waiOpen = !compactSessionObserverStatus.workingAllianceCollapsed;
  const analyticsOpen = compactSessionObserverStatus.analyticsVisible;

  const emptyMessage = compactSessionObserverStatus.isClientMode
    ? t('report_no_data_client_mode')
    : t('report_no_data_yet');

  return (
    <div className="single-column-observer-tabs" aria-label={t('report_in_session_title')}>
      {compactSessionObserverStatus.showWorkingAlliance && (
        <div className={`single-column-observer-tab single-column-observer-tab--wai${waiOpen ? '' : ' single-column-observer-tab--collapsed'}`}>
          <WorkingAllianceChart
            series={compactSessionObserverStatus.workingAllianceSeries}
            visibleCount={compactSessionObserverStatus.workingAllianceVisibleCount}
            collapsed={!waiOpen}
            onToggle={compactSessionObserverStatus.onToggleWorkingAlliance}
            onInfoOpen={onShowWorkingAlliance}
            viewBoxHeight={160}
          />
        </div>
      )}
      <div className={`single-column-observer-tab single-column-observer-tab--analytics${analyticsOpen ? '' : ' single-column-observer-tab--collapsed'}`}>
        <ReportFullSessionAnalytics
          t={t}
          title={t('report_in_session_title')}
          currentTurnMessages={compactSessionObserverStatus.currentTurnMessages}
          displayStageMetrics={compactSessionObserverStatus.displayStageMetrics}
          hasStageMetricsData={compactSessionObserverStatus.hasStageMetricsData}
          streamingActive={compactSessionObserverStatus.streamingActive}
          isViewingLatest={compactSessionObserverStatus.isViewingLatest}
          isClientMode={compactSessionObserverStatus.isClientMode}
          conversationStarted={compactSessionObserverStatus.conversationStarted}
          showClientParameters={compactSessionObserverStatus.isClientMode}
          revealedItems={compactSessionObserverStatus.revealedItems}
          analyticsVisible={analyticsOpen}
          onToggleAnalytics={compactSessionObserverStatus.onToggleAnalytics}
          showVisibilityToggle
          emptyScoringMessage={emptyMessage}
          emptyMetricsMessage={emptyMessage}
          AnalyticsMessageComponent={CompactAnalyticsMessage}
          LoadingDotsComponent={CompactLoadingDots}
          theme={compactSessionObserverStatus.theme}
          turnIndex={compactSessionObserverStatus.turnIndex}
          reportSessionId={compactSessionObserverStatus.reportSessionId}
          guidanceVisible={compactSessionObserverStatus.guidanceVisible}
          onRevealGuidance={compactSessionObserverStatus.onRevealGuidance}
          onHideGuidance={compactSessionObserverStatus.onHideGuidance}
          onShowAutomatedMiscMitiCounsellor={compactSessionObserverStatus.onShowAutomatedMiscMitiCounsellor}
        />
      </div>
    </div>
  );
}

function CompactPostSessionAnalytics({ t, compactPostSessionStatus, onShowWorkingAlliance }) {
  const [openPanels, setOpenPanels] = useState({
    wai: false,
    analytics: true,
  });

  const togglePanel = (panelName) => {
    setOpenPanels((current) => {
      if (current[panelName]) {
        return { ...current, [panelName]: false };
      }
      return { wai: panelName === 'wai', analytics: panelName === 'analytics' };
    });
  };

  const analyticsVisible = openPanels.analytics;

  return (
    <div className="single-column-session-metrics single-column-session-metrics--analytics">
      <div className="single-column-observer-tabs">
        {compactPostSessionStatus.showWorkingAlliance && (
          <div className={`single-column-observer-tab single-column-observer-tab--wai${openPanels.wai ? '' : ' single-column-observer-tab--collapsed'}`}>
            <WorkingAllianceChart
              series={compactPostSessionStatus.workingAllianceSeries}
              visibleCount={compactPostSessionStatus.workingAllianceVisibleCount}
              collapsed={!openPanels.wai}
              onToggle={() => togglePanel('wai')}
              onInfoOpen={onShowWorkingAlliance}
              viewBoxHeight={160}
            />
          </div>
        )}
        <div className={`single-column-observer-tab single-column-observer-tab--analytics${analyticsVisible ? '' : ' single-column-observer-tab--collapsed'}`}>
          <ReportFullSessionAnalytics
            t={t}
            title={t('report_post_session_title')}
            currentTurnMessages={compactPostSessionStatus.currentTurnMessages}
            displayStageMetrics={compactPostSessionStatus.displayStageMetrics}
            hasStageMetricsData={compactPostSessionStatus.hasStageMetricsData}
            conversationStarted
            isClientMode={compactPostSessionStatus.isClientMode}
            showClientParameters={compactPostSessionStatus.isClientMode}
            revealedItems={compactPostSessionStatus.revealedItems}
            analyticsVisible={analyticsVisible}
            onToggleAnalytics={() => togglePanel('analytics')}
            showVisibilityToggle
            emptyScoringMessage={t('report_no_data_this_turn')}
            emptyMetricsMessage={t('report_no_metrics_available')}
            AnalyticsMessageComponent={CompactAnalyticsMessage}
            LoadingDotsComponent={CompactLoadingDots}
            theme={compactPostSessionStatus.theme}
            turnIndex={compactPostSessionStatus.turnIndex}
            reportSessionId={compactPostSessionStatus.reportSessionId}
            guidanceVisible={compactPostSessionStatus.guidanceVisible}
            onRevealGuidance={compactPostSessionStatus.onRevealGuidance}
            onHideGuidance={compactPostSessionStatus.onHideGuidance}
            onShowAutomatedMiscMitiCounsellor={compactPostSessionStatus.onShowAutomatedMiscMitiCounsellor}
          />
        </div>
      </div>
      <div className="single-column-session-action-dock">
        <div className="oars-action-bar">
          <div className="oars-next-exercise-wrap">
            <button
              type="button"
              className="oars-btn oars-btn--primary"
              onClick={compactPostSessionStatus.onBackToMetrics}
            >
              {t('report_back_to_metrics')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CompactPostSessionMetrics({ t, compactPostSessionStatus, onShowWorkingAlliance }) {
  const analysisStatus = compactPostSessionStatus?.analysisStatus;
  if (!analysisStatus) {
    return null;
  }

  if (compactPostSessionStatus.postSessionView === 'analytics') {
    return (
      <CompactPostSessionAnalytics
        t={t}
        compactPostSessionStatus={compactPostSessionStatus}
        onShowWorkingAlliance={onShowWorkingAlliance}
      />
    );
  }

  return (
    <div className="single-column-session-metrics">
      <div className="single-column-session-metrics-scroll">
        {analysisStatus.end_reason && (
          <div className="single-column-session-end-reason">
            <span className="single-column-session-end-reason__label">{t('report_session_end_reason')}</span>
            <p className="single-column-session-end-reason__text">{analysisStatus.end_reason}</p>
          </div>
        )}
        <ReportDownloadActions
          t={t}
          theme={compactPostSessionStatus.theme}
          isClientMode={compactPostSessionStatus.isClientMode}
          showDownloadCue={compactPostSessionStatus.showDownloadCue}
          showDownloadNotice={compactPostSessionStatus.showDownloadNotice}
          onCloseDownloadNotice={compactPostSessionStatus.onCloseDownloadNotice}
          pdfComplete={analysisStatus.pdf_complete}
          onDownloadPdf={compactPostSessionStatus.onDownloadPdf}
          onDownloadCsv={compactPostSessionStatus.onDownloadCsv}
        />
      </div>
      {(compactPostSessionStatus.showHistoricalReplayActions || compactPostSessionStatus.showLiveSessionActions) && (
        <div className="single-column-session-action-dock">
          <div className="oars-action-bar">
            <button
              type="button"
              className={`oars-btn oars-btn--secondary${
                (compactPostSessionStatus.showReturnToAccountCue || compactPostSessionStatus.showStartNewSessionCue)
                  ? ' oars-btn--selection-cue'
                  : ''
              }`}
              data-guided-highlight-purpose={
                compactPostSessionStatus.showReturnToAccountCue
                  ? 'Return to Account to review this saved session.'
                  : compactPostSessionStatus.showStartNewSessionCue
                    ? 'Start another Full Session with a client.'
                    : undefined
              }
              onClick={
                compactPostSessionStatus.showLiveSessionActions
                  ? compactPostSessionStatus.onRestartSession
                  : compactPostSessionStatus.onReturnToAccount
              }
              disabled={
                compactPostSessionStatus.showLiveSessionActions
                  ? !compactPostSessionStatus.liveMetricsActionsEnabled
                  : false
              }
            >
              {compactPostSessionStatus.showLiveSessionActions
                ? t('report_start_new_session_btn')
                : t('account_return_to_account')}
            </button>
            <div className="oars-next-exercise-wrap">
              <button
                type="button"
                className="oars-btn oars-btn--primary"
                onClick={compactPostSessionStatus.onReviewAnalytics}
                disabled={
                  !compactPostSessionStatus.canReviewAnalytics ||
                  (compactPostSessionStatus.showLiveSessionActions && !compactPostSessionStatus.liveMetricsActionsEnabled)
                }
              >
                {t('report_review_analytics_btn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ClientSidebar({
  theme = 'dark',
  selectedProfile,
  showPostDownloadNextStepCue = false,
  disableFullSessionCues = false,
  disableSpecificSkillsStageOneCue = false,
  suppressTransientNotices = false,
  onProfileSelect,
  collapsed,
  onToggleCollapse,
  onShowClientInfo,
  onShowCounsellors,
  onCustomClient,
  clientModeCounsellorMode = null,
  onClientModeCounsellorModeChange,
  showBtcCounsellorCue = false,
  oarsExercise = null,
  oarsExerciseLoading = false,
  oarsAttempt = null,
  oarsAnalyzing = false,
  oarsStreamingEvaluation = null,
  showSpecificSkillsPostAttemptCue = false,
  showSpecificSkillsActionCue = showSpecificSkillsPostAttemptCue,
  oarsFilter = undefined,
  onOarsFilterChange,
  onOarsInfoOpen,
  onStrategyInfoOpen,
  oarsInfoOpen = false,
  authenticated = false,
  strategiesSelectedTask = null,
  strategiesSelectedStrategy = null,
  strategiesExerciseStatus = 'idle',
  onStrategiesTaskChange,
  onStrategiesStrategyChange,
  strategiesFlow = null,
  showStrategiesDrawerDetails = false,
  onSpecificStrategyTryAgain,
  onStrategiesNextExercise,
  isSpecificStrategyReplay = false,
  onReturnToAccountFromSpecificStrategyReplay,
  hideHeader = false,
  hideCustomClientOption = false,
  compactPostSessionStatus = null,
  compactSessionObserverStatus = null,
  fullSessionVignette = null,
  showFullSessionClientDescription = false,
  showOarsConversationContext = false,
  onOarsNextExercise,
  onOarsTryAgain,
  isSpecificSkillReplay = false,
  onReturnToAccountFromSpecificSkillReplay,
  onShowAutomatedMiscMitiCounsellor,
  onShowWorkingAlliance,
  className = '',
}) {
  const { t } = useLanguage();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRandomizing, setIsRandomizing] = useState(false);
  const [isOarsDropdownOpen, setIsOarsDropdownOpen] = useState(false);
  const [isBtcDropdownOpen, setIsBtcDropdownOpen] = useState(false);
  const [previewProfile, setPreviewProfile] = useState(selectedProfile);
  const oarsDropdownRef = useRef(null);
  const btcDropdownRef = useRef(null);
  const commitTimeoutRef = useRef(null);
  const selectedProfileRef = useRef(selectedProfile);
  const btcCounsellorOptions = CLIENT_MODE_COUNSELLOR_OPTIONS;
  const selectedBtcCounsellor =
    btcCounsellorOptions.find((option) => option.value === clientModeCounsellorMode) ||
    null;
  const WheelArrowUp = () => (
    <svg
      className="wheel-scroll-icon"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M4 9l4-4 4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const WheelArrowDown = () => (
    <svg
      className="wheel-scroll-icon"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M4 7l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const ClipboardPencilIcon = () => (
    <svg
      className="upload-icon"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {/* Clipboard frame */}
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      {/* Clip tab */}
      <rect x="8" y="2" width="8" height="4" rx="1" />
      {/* Pencil body */}
      <path d="M9 17l5-5 2 2-5 5H9v-2z" />
      {/* Pencil nib */}
      <path d="M14 12l1-1a1.5 1.5 0 0 1 2 2l-1 1" />
    </svg>
  );

  const DiceIcon = () => (
    <svg
      className="dice-icon"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <g transform="rotate(-18 12 12)">
        <rect x="6" y="6" width="12" height="12" rx="3" />
        <circle cx="9" cy="9" r="1.1" fill="currentColor" stroke="none" />
        <circle cx="15" cy="9" r="1.1" fill="currentColor" stroke="none" />
        <circle cx="9" cy="15" r="1.1" fill="currentColor" stroke="none" />
        <circle cx="15" cy="15" r="1.1" fill="currentColor" stroke="none" />
        <circle cx="12" cy="12" r="1.1" fill="currentColor" stroke="none" />
      </g>
      <path d="M4 9c-1.2-0.6-2-1.6-2.4-2.7" />
      <path d="M6 5c-0.6-1.1-0.9-2.2-0.8-3.3" />
    </svg>
  );

  useEffect(() => {
    if (!authenticated) {
      setProfiles([]);
      setLoading(false);
      setError(null);
      return;
    }
    loadProfiles();
  }, [authenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (oarsDropdownRef.current && !oarsDropdownRef.current.contains(event.target)) {
        setIsOarsDropdownOpen(false);
      }
      if (btcDropdownRef.current && !btcDropdownRef.current.contains(event.target)) {
        setIsBtcDropdownOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOarsDropdownOpen(false);
        setIsBtcDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  useEffect(() => {
    selectedProfileRef.current = selectedProfile;
    setPreviewProfile(selectedProfile);
  }, [selectedProfile]);

  useEffect(() => {
    return () => {
      if (commitTimeoutRef.current) {
        clearTimeout(commitTimeoutRef.current);
      }
    };
  }, []);

  const loadProfiles = async () => {
    try {
      setLoading(true);
      const data = await getProfiles();
      setProfiles(data.profiles);
      setError(null);
    } catch (err) {
      setError(t('sidebar_error_load'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const commitProfileSelection = (profileId, immediate = false) => {
    if (commitTimeoutRef.current) {
      clearTimeout(commitTimeoutRef.current);
      commitTimeoutRef.current = null;
    }

    if (immediate) {
      Promise.resolve(onProfileSelect(profileId)).then((didSelect) => {
        if (didSelect === false) {
          setPreviewProfile(selectedProfileRef.current);
        }
      });
      return;
    }

    commitTimeoutRef.current = setTimeout(() => {
      commitTimeoutRef.current = null;
      if (selectedProfileRef.current !== profileId) {
        Promise.resolve(onProfileSelect(profileId)).then((didSelect) => {
          if (didSelect === false) {
            setPreviewProfile(selectedProfileRef.current);
          }
        });
      }
    }, 350);
  };

  const previewAndCommitProfile = (profileId, immediate = false) => {
    setPreviewProfile(profileId);
    commitProfileSelection(profileId, immediate);
  };

  const handleRandomClient = async () => {
    if (isRandomizing || profiles.length === 0) {
      return;
    }

    setIsRandomizing(true);
    const candidateProfiles =
      selectedProfile !== null && profiles.length > 1
        ? profiles.filter((profile) => profile.id !== selectedProfile)
        : profiles;
    const randomIndex = Math.floor(Math.random() * candidateProfiles.length);
    const randomProfileId = candidateProfiles[randomIndex].id;
    previewAndCommitProfile(randomProfileId, true);

    // Debounce: prevent spam clicking for 500ms
    setTimeout(() => {
      setIsRandomizing(false);
    }, 500);
  };




  const selectAdjacentClient = (direction) => {
    if (profiles.length === 0) {
      return;
    }
    const referenceId = previewProfile !== null ? previewProfile : selectedProfile;
    const currentIndex = referenceId !== null
      ? profiles.findIndex((profile) => profile.id === referenceId)
      : -1;

    let nextIndex;
    if (currentIndex === -1) {
      nextIndex = direction > 0 ? 0 : profiles.length - 1;
    } else {
      nextIndex = (currentIndex + direction + profiles.length) % profiles.length;
    }

    const nextProfileId = profiles[nextIndex].id;
    previewAndCommitProfile(nextProfileId);
  };

  const handleWheelKeyDown = (event) => {
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      selectAdjacentClient(1);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      selectAdjacentClient(-1);
    }
  };

  const handleWheelScroll = (event) => {
    if (profiles.length === 0 || event.deltaY === 0) {
      return;
    }
    event.preventDefault();
    if (event.deltaY < 0) {
      selectAdjacentClient(1);
    } else if (event.deltaY > 0) {
      selectAdjacentClient(-1);
    }
  };

  const displayProfileId = previewProfile !== null ? previewProfile : selectedProfile;
  const currentProfile = profiles.find((profile) => profile.id === displayProfileId);
  const currentClientNumber = currentProfile ? currentProfile.id : null;
  const minProfileId = profiles.length ? Math.min(...profiles.map((profile) => profile.id)) : null;
  const maxProfileId = profiles.length ? Math.max(...profiles.map((profile) => profile.id)) : null;
  const showOarsGuidedHighlight =
    (authenticated && !disableSpecificSkillsStageOneCue && !oarsExercise && !oarsInfoOpen) ||
    (!!oarsAttempt && !oarsAnalyzing && showSpecificSkillsPostAttemptCue);
  const showBtcGuidedHighlight = authenticated && showBtcCounsellorCue;
  const showClientGuidedHighlight =
    authenticated && !disableFullSessionCues && (selectedProfile == null || showPostDownloadNextStepCue);
  const sidebarClasses = [
    'client-sidebar',
    collapsed ? 'collapsed' : '',
    className,
  ].filter(Boolean).join(' ');

  if (loading) {
    return (
      <div className={sidebarClasses}>
        {!collapsed && <div className="sidebar-loading">{t('sidebar_loading')}</div>}
      </div>
    );
  }

  if (error) {
    return (
      <div className={sidebarClasses}>
        {!collapsed && (
          <div className="sidebar-error">
            <p>{error}</p>
            <button onClick={loadProfiles}>{t('sidebar_retry')}</button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={sidebarClasses}>
      {!collapsed && (
        <>
          {!hideHeader && (
            <ClientSidebarHeader
              t={t}
              selectedProfile={selectedProfile}
              onShowClientInfo={onShowClientInfo}
              onOarsInfoOpen={onOarsInfoOpen}
              onStrategyInfoOpen={onStrategyInfoOpen}
              onShowCounsellors={onShowCounsellors}
            />
          )}

          {selectedProfile === -4 ? (
            <SpecificStrategiesSidebarPanel
              WheelArrowUp={WheelArrowUp}
              WheelArrowDown={WheelArrowDown}
              showGuidedHighlights={authenticated && !disableSpecificSkillsStageOneCue}
              selectedTask={strategiesSelectedTask}
              selectedStrategy={strategiesSelectedStrategy}
              exerciseStatus={strategiesExerciseStatus}
              onTaskChange={onStrategiesTaskChange}
              onStrategyChange={onStrategiesStrategyChange}
              showDrawerDetails={showStrategiesDrawerDetails}
              theme={theme}
              t={t}
              strategiesFlow={strategiesFlow}
              onTryAgain={onSpecificStrategyTryAgain}
              onNextExercise={onStrategiesNextExercise}
              isReplay={isSpecificStrategyReplay}
              onReturnToAccount={onReturnToAccountFromSpecificStrategyReplay}
              onShowAutomatedMiscMitiCounsellor={onShowAutomatedMiscMitiCounsellor}
            />
          ) : selectedProfile === -3 ? (
            <OarsSidebarPanel
              theme={theme}
              t={t}
              oarsExercise={oarsExercise}
              oarsExerciseLoading={oarsExerciseLoading}
              oarsAttempt={oarsAttempt}
              oarsAnalyzing={oarsAnalyzing}
              oarsStreamingEvaluation={oarsStreamingEvaluation}
              showConversationContext={showOarsConversationContext}
              onOarsNextExercise={onOarsNextExercise}
              onOarsTryAgain={onOarsTryAgain}
              isSpecificSkillReplay={isSpecificSkillReplay}
              suppressLectureReminder={suppressTransientNotices}
              onReturnToAccount={onReturnToAccountFromSpecificSkillReplay}
              onShowAutomatedMiscMitiCounsellor={onShowAutomatedMiscMitiCounsellor}
              showOarsGuidedHighlight={showOarsGuidedHighlight}
              showSpecificSkillsPostAttemptCue={showSpecificSkillsActionCue}
              oarsDropdownRef={oarsDropdownRef}
              isOarsDropdownOpen={isOarsDropdownOpen}
              setIsOarsDropdownOpen={setIsOarsDropdownOpen}
              oarsFilter={oarsFilter}
              onOarsFilterChange={onOarsFilterChange}
              WheelArrowUp={WheelArrowUp}
              WheelArrowDown={WheelArrowDown}
              DiceIcon={DiceIcon}
            />
          ) : selectedProfile === -2 ? (
            <>
              <BtcCounsellorSelector
                t={t}
                showBtcGuidedHighlight={showBtcGuidedHighlight}
                isBtcDropdownOpen={isBtcDropdownOpen}
                setIsBtcDropdownOpen={setIsBtcDropdownOpen}
                btcDropdownRef={btcDropdownRef}
                selectedBtcCounsellor={selectedBtcCounsellor}
                btcCounsellorOptions={btcCounsellorOptions}
                clientModeCounsellorMode={clientModeCounsellorMode}
                onClientModeCounsellorModeChange={onClientModeCounsellorModeChange}
                WheelArrowUp={WheelArrowUp}
                WheelArrowDown={WheelArrowDown}
              />
              {compactPostSessionStatus && (
                <CompactPostSessionMetrics
                  t={t}
                  compactPostSessionStatus={compactPostSessionStatus}
                  onShowWorkingAlliance={onShowWorkingAlliance}
                />
              )}
              {!compactPostSessionStatus && compactSessionObserverStatus && (
                <CompactSessionObserverTabs
                  t={t}
                  compactSessionObserverStatus={compactSessionObserverStatus}
                  onShowWorkingAlliance={onShowWorkingAlliance}
                />
              )}
            </>
          ) : (
            <>
              <ClientWheelSelector
                theme={theme}
                t={t}
                showClientGuidedHighlight={showClientGuidedHighlight}
                currentClientNumber={currentClientNumber}
                minProfileId={minProfileId}
                maxProfileId={maxProfileId}
                handleWheelScroll={handleWheelScroll}
                handleWheelKeyDown={handleWheelKeyDown}
                selectAdjacentClient={selectAdjacentClient}
                WheelArrowUp={WheelArrowUp}
                WheelArrowDown={WheelArrowDown}
                handleRandomClient={handleRandomClient}
                isRandomizing={isRandomizing}
                DiceIcon={DiceIcon}
                onCustomClient={onCustomClient}
                ClipboardPencilIcon={ClipboardPencilIcon}
                hideCustomClientOption={hideCustomClientOption}
                suppressLectureReminder={suppressTransientNotices}
              />
              {showFullSessionClientDescription && fullSessionVignette?.patient_description && (
                <div className="oars-context-panel oars-sidebar-context-panel">
                  <div className="analytics-header">
                    <h4 className="analytics-title">Client Description</h4>
                    <p className="analytics-subtitle">Background on the client for this exercise</p>
                  </div>
                  <div className="oars-context-body">
                    <div>{fullSessionVignette.patient_description}</div>
                  </div>
                </div>
              )}
              {compactPostSessionStatus && (
                <CompactPostSessionMetrics
                  t={t}
                  compactPostSessionStatus={compactPostSessionStatus}
                  onShowWorkingAlliance={onShowWorkingAlliance}
                />
              )}
              {!showFullSessionClientDescription && !compactPostSessionStatus && compactSessionObserverStatus && (
                <CompactSessionObserverTabs
                  t={t}
                  compactSessionObserverStatus={compactSessionObserverStatus}
                  onShowWorkingAlliance={onShowWorkingAlliance}
                />
              )}
            </>
          )}
        </>
      )}

    </div>
  );
}

export default ClientSidebar;
